/**
 * Tooltip:
 * - Normal hover: attaches to a trigger, shows after delay, flips/clamps to viewport
 * - Programmatic (chart): ExternalEnterAt/ExternalLeave with same delay + clamping
 */
export class Tooltip {
    private readonly hoverEl: HTMLElement;
    private readonly spacerEl: HTMLElement;
    private readonly tooltipEl: HTMLElement;
    private hoverTimer: number | null = null;
    private isTooltipVisible: boolean = false;
    private readonly isInteractive: boolean;
    private readonly isManual: boolean;
    private hideTimer: number | null = null;
    private originalElementPosition: { top: number; left: number } | null = null;
    private tooltipOffset: { top: number; left: number } | null = null;

    // For programmatic (chart) flow
    private externalAnchorEl: HTMLElement | null = null;
    private latestCursor: { x: number; y: number } | null = null;
    private trackMoveHandler: ((e: MouseEvent) => void) | null = null;

    /**
     * Creates a new Tooltip instance.
     * @param tooltipContainerEl The container element for the tooltip (trigger wrapper or spacer in manual mode).
     * @param isInteractive Whether the tooltip is interactive (hoverable).
     * @param isManual Whether the tooltip is in manual mode (programmatic control).
     */
    constructor(tooltipContainerEl: HTMLElement, isInteractive: boolean, isManual: boolean) {
        this.isInteractive = isInteractive;
        this.isManual = isManual;

        if (this.isManual === true) {
            // In manual mode, the element *is* the spacer (.tooltip-spacer)
            this.spacerEl = tooltipContainerEl;
            this.hoverEl = tooltipContainerEl; // not actually used for hover wiring in manual mode
        } else {
            // Non-manual: el is the tooltip container (trigger wrapper)
            this.hoverEl = tooltipContainerEl;
            const spacer = this.isInteractive
                ? tooltipContainerEl.querySelector('.tooltip-spacer')
                : tooltipContainerEl.nextElementSibling;

            if ((spacer instanceof HTMLElement) === false) {
                throw new Error('Tooltip: .tooltip-spacer not found');
            }
            this.spacerEl = spacer;
        }

        const content = this.spacerEl.querySelector('.tooltip-content');
        if ((content instanceof HTMLElement) === false) {
            throw new Error('Tooltip: .tooltip-content not found');
        }
        this.tooltipEl = content;

        if (this.isManual === false) {
            this.attachHoverEvents();
        }
    }

    /**
     * Use when in manual mode to show the tooltip at specific coordinates relative to an anchor element.
     * @param anchorEl The element to anchor the tooltip position to.
     * @param clientX The X coordinate (viewport) to position the tooltip near.
     * @param clientY The Y coordinate (viewport) to position the tooltip near.
     */
    public ExternalEnterAt(anchorEl: HTMLElement, clientX: number, clientY: number): void {
        this.externalAnchorEl = anchorEl;

        if (this.hoverTimer !== null) {
            clearTimeout(this.hoverTimer);
        }
        if (this.hideTimer !== null) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }

        this.startCursorTracking();
        this.hoverTimer = window.setTimeout(() => {
            this.showTooltipAt(clientX, clientY, anchorEl);
        }, 500); // match normal tooltip delay
    }

    /**
     * UJse when in manual mode to hide the tooltip.
     * @returns {void}
     */
    public ExternalLeave(): void {
        this.stopCursorTracking();

        if (this.isInteractive === false) {
            this.hideTooltip();
            return;
        }
        this.hideTimer = window.setTimeout(() => {
            if (this.isMouseOverTooltip() === false && this.isMouseOverTrigger() === false) {
                this.hideTooltip();
            }
        }, 100);
    }

    private attachHoverEvents(): void {
        this.hoverEl.addEventListener('mouseenter', this.onMouseEnter);
        this.hoverEl.addEventListener('mouseleave', this.onMouseLeave);

        if (this.isInteractive) {
            this.spacerEl.addEventListener('mouseenter', this.onTooltipEnter);
            this.spacerEl.addEventListener('mouseleave', this.onTooltipLeave);
        }
    }

    private onMouseEnter = (event: MouseEvent): void => {
        if (this.hoverTimer !== null) {
            clearTimeout(this.hoverTimer);
        }
        if (this.hideTimer !== null) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }

        this.startCursorTracking();

        // Standard tooltip delay
        this.hoverTimer = window.setTimeout(() => {
            this.showTooltip(event);
        }, 500);
    };

    private onMouseLeave = (): void => {
        if (this.hoverTimer !== null) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = null;
        }

        this.stopCursorTracking();

        if (!this.isInteractive) {
            this.hideTooltip();
        } else {
            this.hideTimer = window.setTimeout(() => {
                if (!this.isMouseOverTooltip() && !this.isMouseOverTrigger()) {
                    this.hideTooltip();
                }
            }, 100);
        }
    };

    private onTooltipEnter = (): void => {
        if (this.hideTimer !== null) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    };

    private onTooltipLeave = (): void => {
        this.hideTimer = window.setTimeout(() => {
            if (!this.isMouseOverTooltip() && !this.isMouseOverTrigger()) {
                this.hideTooltip();
            }
        }, 100);
    };

    private isMouseOverTrigger(): boolean {
        try {
            const el = this.externalAnchorEl ?? this.hoverEl;
            return el.matches(':hover');
        } catch {
            return false;
        }
    }

    private isMouseOverTooltip(): boolean {
        try {
            return this.spacerEl.matches(':hover');
        } catch {
            return false;
        }
    }

    private showTooltip(event: MouseEvent): void {
        if (this.isMouseOverTrigger() === false && this.isMouseOverTooltip() === false) {
            this.stopCursorTracking();
            return;
        }

        const cx = this.latestCursor?.x ?? event.clientX;
        const cy = this.latestCursor?.y ?? event.clientY;

        const tooltipRect = this.measureTooltipRect();
        const elementRect = this.hoverEl.getBoundingClientRect();

        this.originalElementPosition = { top: elementRect.top, left: elementRect.left };

        const coords = this.calculatePosition(cx, cy, tooltipRect);
        this.tooltipOffset = {
            top: coords.top - elementRect.top,
            left: coords.left - elementRect.left
        };

        this.applyStyles(coords.top, coords.left);

        // Portal to body to avoid stacking/transform issues
        if (this.spacerEl.parentElement !== document.body) {
            document.body.appendChild(this.spacerEl);
        }

        this.spacerEl.classList.add('tooltip-spacer--visible');
        this.isTooltipVisible = true;

        this.stopCursorTracking();

        window.addEventListener('scroll', this.updateTooltipPosition, true);
        window.addEventListener('resize', this.updateTooltipPosition, true);
    }

    private showTooltipAt(clientX: number, clientY: number, anchorEl: HTMLElement): void {
        if (this.isMouseOverTrigger() === false && this.isMouseOverTooltip() === false) {
            this.stopCursorTracking();
            return;
        }

        const cx = this.latestCursor?.x ?? clientX;
        const cy = this.latestCursor?.y ?? clientY;

        const tooltipRect = this.measureTooltipRect();
        const elementRect = anchorEl.getBoundingClientRect();

        this.originalElementPosition = { top: elementRect.top, left: elementRect.left };

        const coords = this.calculatePosition(cx, cy, tooltipRect);
        this.tooltipOffset = {
            top: coords.top - elementRect.top,
            left: coords.left - elementRect.left
        };

        this.applyStyles(coords.top, coords.left);

        if (this.spacerEl.parentElement !== document.body) {
            document.body.appendChild(this.spacerEl);
        }

        this.spacerEl.classList.add('tooltip-spacer--visible');
        this.isTooltipVisible = true;

        this.stopCursorTracking();

        window.addEventListener('scroll', this.updateTooltipPosition, true);
        window.addEventListener('resize', this.updateTooltipPosition, true);
    }

    private hideTooltip(): void {
        this.spacerEl.classList.remove('tooltip-spacer--visible');

        // Move back to original parent if we portaled
        if (this.spacerEl.parentElement === document.body) {
            const originalParent = this.isInteractive ? this.hoverEl : this.hoverEl.parentElement;
            if (originalParent) {
                originalParent.appendChild(this.spacerEl);
            }
        }

        this.isTooltipVisible = false;
        this.originalElementPosition = null;
        this.tooltipOffset = null;

        window.removeEventListener('scroll', this.updateTooltipPosition, true);
        window.removeEventListener('resize', this.updateTooltipPosition, true);

        if (this.hideTimer !== null) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }

    private updateTooltipPosition = (): void => {
        if (this.isTooltipVisible === false || !this.tooltipOffset || !this.originalElementPosition) return;

        if (this.isInteractive && this.isMouseOverTrigger() === false && this.isMouseOverTooltip() === false) {
            return;
        }

        const currentElementRect = this.hoverEl.getBoundingClientRect();

        // Keep the same relative offset; allow natural off-screen during scroll like native tooltips
        const newTop = currentElementRect.top + this.tooltipOffset.top;
        const newLeft = currentElementRect.left + this.tooltipOffset.left;

        this.applyStyles(newTop, newLeft);
    };

    private startCursorTracking(): void {
        if (this.trackMoveHandler) return;
        this.trackMoveHandler = (ev: MouseEvent) => {
            this.latestCursor = { x: ev.clientX, y: ev.clientY };
        };
        document.addEventListener('mousemove', this.trackMoveHandler, { passive: true });
    }

    private stopCursorTracking(): void {
        if (!this.trackMoveHandler) return;
        document.removeEventListener('mousemove', this.trackMoveHandler);
        this.trackMoveHandler = null;
        this.latestCursor = null;
    }

    private calculatePosition(x: number, y: number, tooltipRect: DOMRect): { top: number; left: number } {
        const cursorPad = 12;
        let left = x + cursorPad;
        let top = y + cursorPad;

        // horizontal flip if overflowing right
        const maxLeft = window.innerWidth - tooltipRect.width;
        if (left > maxLeft) {
            left = x - tooltipRect.width - cursorPad;
        }
        left = Math.max(0, Math.min(left, maxLeft));

        // vertical flip if overflowing bottom
        const maxTop = window.innerHeight - tooltipRect.height;
        if (top > maxTop) {
            top = y - tooltipRect.height - cursorPad;
        }
        top = Math.max(0, Math.min(top, maxTop));

        return { top, left };
    }

    private applyStyles(top: number, left: number): void {
        this.spacerEl.style.top = `${top}px`;
        this.spacerEl.style.left = `${left}px`;
    }

    private measureTooltipRect(): DOMRect {
        // If the tooltip is not visible, temporarily show it (offscreen/hidden) to get a real size.
        const wasVisible = this.spacerEl.classList.contains('tooltip-spacer--visible');
        const previousVisibility = this.spacerEl.style.visibility;

        if (wasVisible === false) {
            // Ensure it exists in the DOM where styles apply; portaling to body avoids transform/overflow parents.
            if (this.spacerEl.parentElement !== document.body) {
                document.body.appendChild(this.spacerEl);
            }
            // Make it render layout without being seen by the user
            this.spacerEl.classList.add('tooltip-spacer--visible');
            this.spacerEl.style.visibility = 'hidden';
        }

        const rect = this.tooltipEl.getBoundingClientRect();

        // Restore the prior visibility state (we only wanted to measure)
        if (wasVisible === false) {
            this.spacerEl.classList.remove('tooltip-spacer--visible');
            this.spacerEl.style.visibility = previousVisibility;
        }

        return rect;
    }
}

// ---- Registry the chart/Blazor talks to ----
const TooltipRegistry = new Map<string, Tooltip>();

window['RegisterTooltipV2'] = (id: string, el: HTMLElement, isInteractive: boolean, isManual: boolean) => {
    if (TooltipRegistry.has(id) === false) {
        TooltipRegistry.set(id, new Tooltip(el, isInteractive, isManual));
    }
};

window['UnregisterTooltipV2'] = (id: string) => {
    TooltipRegistry.delete(id);
};

window['TooltipExternalEnterAt'] = (id: string, anchorEl: HTMLElement, clientX: number, clientY: number) => {
    const inst = TooltipRegistry.get(id);
    if (inst) {
        inst.ExternalEnterAt(anchorEl, clientX, clientY);
    }
};

window['TooltipExternalLeave'] = (id: string) => {
    const inst = TooltipRegistry.get(id);
    if (inst) {
        inst.ExternalLeave();
    }
};